import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  Card,
  TableRow,
  Paper,
  IconButton,
} from "@mui/material"
import ClearIcon from "@mui/icons-material/Clear"
import SingleSelect from "@/components/Select/SingleSelect"
import { useCustomQuery } from "@/hooks/useCustomQuery"
import BillingDetails from "./BillingDetails"
import SecondaryButton from "../Buttons/SecondaryButton"
import useProductable from "@/hooks/useProductable"
import { z } from "zod"
import CustomAutoComplete from "../Select/CustomAutoComplete"
import React, { useImperativeHandle, useRef, useEffect, useState } from "react"
import ClassicSelect from "../Select/ClassicSelect"
import BarcodeInput from "../BarcodeInput"
import { toast } from "sonner"
import { apiFetch } from "@/hooks/apiFetch"
import { isWeightable } from "@/lib/utils"
import { useDispatch, useSelector } from "react-redux"
import useProducts from "@/hooks/useProducts"
import {
  addItem,
  clearItems,
  removeItem,
  updateItemQuantity,
  updateItemDiscount,
  resetOrder,
  updateSellPrice,
} from "@/redux/slices/orderFormSlice"
import { useAppSelector } from "@/lib/hooks"
import {
  playBarcodeErrorSound,
  prepareBarcodeErrorSound,
} from "@/utilities/barcodeErrorSound"

export const ProductableSchema = z.object({
  id: z.number(),
  name: z.string().min(3),
  code: z.string().min(1),
  quantity: z.number(),
  sell_price: z.number(),
  discount: z.number().default(0),
})

// Wrap the component with React.forwardRef
const Productable = React.forwardRef(
  (
    {
      controller,
      control,
      errors,
      onClose,
      name,
      helperText,
      label = "Products",
      defaultProducts = [],
    },
    ref
  ) => {
    const Controller = controller

    const dispatch = useDispatch()
    const { items: orderItems, totalItems } = useAppSelector(
      (state) => state.order
    )

    const [productSearchQuery, setProductSearchQuery] = useState("")
    const { products, isLoading: productLoading } = useProducts({
      searchQuery: productSearchQuery,
    })

    const handleAddedOrderItem = (product) => {
      dispatch(addItem(product))
    }

    const handleAddedItemViaBarcode = (product) => {
      dispatch(addItem(product))
    }

    const handleSellPriceChange = (index, sellPrice) => {
      dispatch(updateSellPrice({ index, sellPrice }))
    }

    const handleQuantityChange = (index, quantity) => {
      dispatch(updateItemQuantity({ index, quantity }))
    }

    const handleItemDiscountChange = (index, discountPercent) => {
      dispatch(updateItemDiscount({ index, discountPercent }))
    }

    const handleRemoveOrderItem = (item) => {
      dispatch(removeItem(item.id))
    }

    const handleReset = () => {
      dispatch(resetOrder())
    }

    const fetchProduct = async (barcode) => {
      prepareBarcodeErrorSound()
      try {
        const response = await apiFetch("product/sku", {
          method: "POST",
          body: JSON.stringify({ sku: barcode }),
        })

        if (response?.data) {
          handleAddedItemViaBarcode(response.data)
        } else {
          playBarcodeErrorSound()
          toast.error("No product found.")
        }
      } catch (error) {
        playBarcodeErrorSound()
        toast.error("No product found.")
      }
    }

    return (
      <div>
        <Typography
          id="modal-modal-title"
          variant="h6"
          sx={{
            fontWeight: 500,
            fontSize: { xs: "12px", md: "16px" },
            mb: 2,
            mt: 1,
            color: "black",
          }}
        >
          {label}
        </Typography>
        <div className="w-full flex flex-col md:flex-row md:items-end md:gap-4 space-y-4 md:space-y-0">
          <div className="w-full md:w-4/12">
            <CustomAutoComplete
              label="Product"
              options={products}
              loading={productLoading}
              error={!!errors[name]}
              helperText={errors[name]?.message}
              optionLabel="sku"
              optionValue="id"
              returnObject={true}
              onChange={(value) => {
                handleAddedOrderItem(value)
              }}
              onInputChange={(searchText, value, type) => {
                if (type == "input") {
                  setProductSearchQuery(searchText)
                }
              }}
            />
          </div>

          {/* Barcode Input */}
          <div className="w-full md:w-5/12">
            <BarcodeInput
              onScan={(value) => {
                if (value) {
                  fetchProduct(value)
                }
              }}
            />
          </div>

          <div className="w-full md:w-1/12">
            <div className="border border-gray-800 rounded-md h-full w-full flex flex-col items-center">
              <div>Products</div>
              <strong>{totalItems}</strong>
            </div>
          </div>

          {/* Reset Button */}
          <div className="w-full md:w-2/12">
            <SecondaryButton
              fullWidth
              label="Reset"
              onClick={handleReset}
              color="secondary"
            />
          </div>
        </div>

        {/* Table and Billing */}
        <div className="mt-4">
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "none",
              borderRadius: "7px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
            }}
            className="rmui-table border"
          >
            <Table aria-label="table">
              <TableHead className="bg-f6f7f9">
                <TableRow>
                  <TableCell>S.N.</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Quantity</TableCell>
                  {isWeightable() && <TableCell>Weight</TableCell>}
                  <TableCell>Price</TableCell>
                  <TableCell>Discount %</TableCell>
                  <TableCell>Total</TableCell>
                  <TableCell>Remove</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <Controller
                  name={name}
                  control={control}
                  defaultValue={[]}
                  render={({ field: { onChange: fieldOnChange } }) => {
                    useEffect(() => {
                      fieldOnChange(orderItems)
                    }, [orderItems])

                    return (
                      <>
                        {orderItems.length > 0 ? (
                          orderItems.map((value, index) => (
                            <TableRow key={index}>
                              <TableCell>{index + 1}</TableCell>
                              <TableCell className="text-nowrap">
                                <Typography variant="body2" color="black">
                                  {value.sku}
                                </Typography>
                                <Typography variant="overline">
                                  {value.name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  sx={{ width: "100px" }}
                                  value={value.quantity}
                                  onFocus={(event) => {
                                    event.target.select()
                                  }}
                                  onChange={(event) => {
                                    handleQuantityChange(
                                      index,
                                      Number(event.target.value)
                                    )
                                  }}
                                  type="number"
                                  inputProps={{ min: 0 }}
                                />
                                <Typography
                                  variant="caption"
                                  gutterBottom
                                  sx={{
                                    display: "block",
                                    color:
                                      value.stock === 0
                                        ? "red"
                                        : value.stock < 10
                                          ? "orange"
                                          : "grey",
                                  }}
                                >
                                  Available: {value.stock}
                                </Typography>
                              </TableCell>
                              {isWeightable() && (
                                <TableCell>
                                  <TextField
                                    size="small"
                                    sx={{ width: "100px" }}
                                    value={value.weight ?? 1}
                                    onFocus={(event) => {
                                      event.target.select()
                                    }}
                                    onChange={(event) =>
                                      handleWeightChange(
                                        index,
                                        event.target.value
                                      )
                                    }
                                    type="number"
                                    inputProps={{ min: 0 }}
                                  />
                                </TableCell>
                              )}
                              <TableCell>
                                <TextField
                                  size="small"
                                  sx={{ width: "100px" }}
                                  value={value.sell_price}
                                  onFocus={(event) => {
                                    event.target.select()
                                  }}
                                  onChange={(event) =>
                                    handleSellPriceChange(
                                      index,
                                      event.target.value
                                    )
                                  }
                                  type="number"
                                  inputProps={{ min: 0 }}
                                />
                                {value.originalPrice && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "block",
                                      color: "success.main",
                                      whiteSpace: "nowrap",
                                    }}
                                  >
                                    <s>Rs. {value.originalPrice}</s>{" "}
                                    {value.discountLabel}
                                  </Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <TextField
                                  size="small"
                                  sx={{ width: "75px" }}
                                  onFocus={(event) => {
                                    event.target.select()
                                  }}
                                  value={value.discountPercent}
                                  onChange={(event) =>
                                    handleItemDiscountChange(
                                      index,
                                      Number(event.target.value)
                                    )
                                  }
                                  type="number"
                                  inputProps={{ min: 0 }}
                                />
                              </TableCell>
                              <TableCell>{value.total}</TableCell>
                              <TableCell>
                                <IconButton
                                  aria-label="remove"
                                  onClick={() => handleRemoveOrderItem(value)}
                                >
                                  <ClearIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        ) : (
                          <TableRow>
                            <TableCell colSpan={7} align="center">
                              No items added yet.
                            </TableCell>
                          </TableRow>
                        )}
                      </>
                    )
                  }}
                />
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    )
  }
)

Productable.displayName = "Productable" // For debugging in React DevTools

export default Productable
